import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@toki/auth';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bell, Check, X, Clock, Sparkle, PaperPlaneTilt, ArrowClockwise, Eye, CaretDown, ChatCircle } from '@phosphor-icons/react';
import {
  createTicketMessage,
  listAssistantSuggestions,
  resolveAssistantSuggestion,
  getAssistantCount,
  getAssistantBriefing,
  scanAssistant,
  executeAssistantAction,
  createAssistantThread,
  postAssistantThreadMessage,
  listAssistantThreads,
  getAssistantThread,
  patchAssistantThread,
  suggestConsultaReply,
  getIncidencia,
  getAdminConsulta,
  listAreas,
  listInternalUsers,
  type AssistantSuggestionApi,
  type AssistantScope,
  type AssistantResolveAction,
  type AssistantActionCard,
  type AssistantThread,
  type AssistantThreadSummary,
  type Area,
  type InternalUser,
} from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { useIsNarrow } from '../hooks/useMediaQuery';
import { getModuleColor } from '../config/modules';
import { getModuleIcon } from '../icons';
import { Topbar } from '../components/Topbar';
import { FilterBar } from '../components/FilterBar';
import { Badge, type BadgeTone } from '../components/Badge';
import { RowTile } from '../components/RowTile';
import styles from './Inbox.module.css';

const mc = getModuleColor('asistente');

/**
 * Urgencia = prioridad, y la prioridad se pinta con la variante `outline`
 * (chip con contorno). Los estados van en `solid`. Así una urgencia media
 * ámbar y un estado ámbar nunca se confunden aunque caigan juntos.
 */
const URGENCY_TONE: Record<string, BadgeTone> = {
  high: 'red',
  medium: 'amber',
  low: 'slate',
};
const URGENCY_LABEL: Record<string, string> = {
  high: 'Urgente',
  medium: 'Media',
  low: 'Baja',
};
// Orden y etiqueta de los grupos de prioridad en la lista de pendientes.
const PRIORITY_ORDER = ['high', 'medium', 'low'] as const;
const URGENCY_GROUP: Record<string, string> = { high: 'ALTA', medium: 'MEDIA', low: 'BAJA' };

const STATUS_LABEL: Record<string, string> = {
  hecha: 'Hecho',
  descartada: 'Descartada',
  mas_tarde: 'Pospuesta',
};
/* 'mas_tarde' pasa de azul a teal: el azul está reservado a acción. */
const STATUS_TONE: Record<string, BadgeTone> = {
  hecha: 'green',
  descartada: 'slate',
  mas_tarde: 'teal',
};

const dtFmt = new Intl.DateTimeFormat('es-ES', {
  timeZone: 'Europe/Madrid',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  return dtFmt.format(new Date(iso));
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 20) return 'Buenas tardes';
  return 'Buenas noches';
}

/** Cajetín de respuesta dentro de una sugerencia "responder_consulta":
 *  pide el borrador de IA al abrir, el técnico lo revisa/edita y envía. */
/** El `origin` de una sugerencia, como módulo del raíl. Las reglas de tickets
 *  dicen `tickets` y el módulo es `incidencias` (el nombre interno): sin esto,
 *  `getModuleColor` caía al de reserva y la sugerencia decía «Configuración». */
function moduloDeOrigen(origin: string): string {
  return origin === 'tickets' ? 'incidencias' : origin;
}

/** «Contestar» de una sugerencia (paso 51). El borrador es el texto LITERAL de
 *  la pregunta frecuente que encaja con el ticket, o vacío; se edita aquí y se
 *  envía con la MISMA llamada que la respuesta del panel (`createTicketMessage`,
 *  pública), así que el correo al cliente, la primera respuesta del SLA y el
 *  ticket congelado se comportan igual. Nunca se envía solo. */
function IncidenciaResponder({
  token,
  incidenciaId,
  borrador,
  faqPregunta,
  busy,
  onSent,
}: {
  token: string;
  incidenciaId: string;
  borrador: string;
  faqPregunta: string | null;
  busy: boolean;
  onSent: () => void;
}) {
  const [text, setText] = useState(borrador);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enviar = async () => {
    setSending(true);
    setError(null);
    try {
      await createTicketMessage(incidenciaId, text.trim(), token, 'public');
      onSent();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.responder}>
      <div className={styles.responderLabel}>
        {faqPregunta && (
          <span className={styles.draftChip}><Sparkle weight="fill" size={11} /> Pregunta frecuente: {faqPregunta}</span>
        )}
        <span className={styles.eyeChip}><Eye weight="fill" size={11} /> Lo verá el cliente</span>
        <span className={styles.responderHint}>{faqPregunta ? 'Léelo antes de enviarlo' : 'Escribe la respuesta'}</span>
      </div>
      <textarea
        className={styles.responderText}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder="Escribe la respuesta que verá el cliente…"
        disabled={busy || sending}
      />
      {error && <div className={styles.actionCardError}>⚠ {error}</div>}
      <div className={styles.responderActions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.primary}`}
          disabled={busy || sending || text.trim().length === 0}
          onClick={enviar}
        >
          <PaperPlaneTilt weight="bold" size={15} /> {sending ? 'Enviando…' : 'Enviar al cliente'}
        </button>
      </div>
    </div>
  );
}

function ConsultaResponder({
  token,
  consultaId,
  busy,
  onSend,
}: {
  token: string;
  consultaId: string;
  busy: boolean;
  onSend: (text: string) => void;
}) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);

  const genDraft = useCallback(async () => {
    setLoading(true);
    try {
      const { draft } = await suggestConsultaReply(consultaId, token);
      setText(draft ?? '');
    } catch {
      setText('');
    } finally {
      setLoading(false);
    }
  }, [consultaId, token]);

  // Al montar (desplegar la sugerencia) pedimos el borrador una vez.
  useEffect(() => { genDraft(); }, [genDraft]);

  return (
    <div className={styles.responder}>
      <div className={styles.responderLabel}>
        <span className={styles.draftChip}><Sparkle weight="fill" size={11} /> Borrador del asistente</span>
        <span className={styles.eyeChip}><Eye weight="fill" size={11} /> Lo verá el ciudadano</span>
        <span className={styles.responderHint}>{loading ? 'Generando borrador…' : 'Léelo antes de enviarlo'}</span>
      </div>
      <textarea
        className={styles.responderText}
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        placeholder={loading ? 'Generando borrador…' : 'Escribe la respuesta que verá el vecino…'}
        disabled={loading || busy}
      />
      <div className={styles.responderActions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.primary}`}
          disabled={busy || loading || text.trim().length === 0}
          onClick={() => onSend(text.trim())}
        >
          <PaperPlaneTilt weight="bold" size={15} /> Enviar al vecino
        </button>
        <button
          type="button"
          className={styles.btn}
          disabled={busy || loading}
          onClick={genDraft}
        >
          <ArrowClockwise weight="bold" size={15} /> {loading ? 'Generando…' : 'Regenerar'}
        </button>
      </div>
    </div>
  );
}

/** Selector inline de una sugerencia "asignar_consulta": elige área (opcional,
 *  se mantiene la del asistente si no la tocas) + técnico, y al asignar el
 *  backend deja ya el aviso de responder. Acción rápida sin salir de la campana. */
function ConsultaAssigner({
  token,
  busy,
  onAssign,
}: {
  token: string;
  busy: boolean;
  onAssign: (areaId: string, techId: string) => void;
}) {
  const [areas, setAreas] = useState<Area[]>([]);
  const [staff, setStaff] = useState<InternalUser[]>([]);
  const [areaId, setAreaId] = useState('');
  const [techId, setTechId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    Promise.all([listAreas(token), listInternalUsers(token)])
      .then(([a, u]) => {
        if (!alive) return;
        setAreas(a.filter((x) => x.isActive));
        setStaff(u.filter((x) => x.isActive));
      })
      .catch(() => { /* Non-fatal: el técnico puede posponer/descartar igual. */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  // Si el técnico elige área, la lista de técnicos se acota a los de esa área.
  const eligible = areaId ? staff.filter((u) => u.areas.some((a) => a.id === areaId)) : staff;

  return (
    <div className={styles.assigner}>
      <div className={styles.assignField}>
        <label className={styles.assignLabel}>Área</label>
        <select
          className={styles.assignSelect}
          value={areaId}
          onChange={(e) => { setAreaId(e.target.value); setTechId(''); }}
          disabled={loading || busy}
        >
          <option value="">Mantener la del asistente</option>
          {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <div className={styles.assignField}>
        <label className={styles.assignLabel}>Técnico</label>
        <select
          className={styles.assignSelect}
          value={techId}
          onChange={(e) => setTechId(e.target.value)}
          disabled={loading || busy}
        >
          <option value="">{loading ? 'Cargando…' : 'Elige un técnico…'}</option>
          {eligible.map((u) => (
            <option key={u.id} value={u.id}>{u.displayName || u.email}</option>
          ))}
        </select>
      </div>
      <div className={styles.responderActions}>
        <button
          type="button"
          className={`${styles.btn} ${styles.primary}`}
          disabled={busy || loading || !techId}
          onClick={() => onAssign(areaId, techId)}
        >
          <Sparkle weight="bold" size={15} /> Asignar y empezar
        </button>
      </div>
    </div>
  );
}

/** Estado de aplicación de las action cards (compartido por todos los hilos):
 *  resultado/errores por `${messageId}:${idx}` y cuál se está aplicando. */
interface ActionApplyState {
  results: Record<string, string>;
  errors: Record<string, string>;
  busy: string | null;
  onApply: (messageId: string, idx: number, actionType: string, params: Record<string, unknown>) => void;
}

/** Detalle real del ticket al que apunta una sugerencia (incidencia o
 *  consulta): la descripción/cuerpo + metadatos. Da al técnico el "de qué va"
 *  sin salir de la campana — clave en el modo foco, donde el contexto de la
 *  regla ("60 días sin cambios") no dice el contenido. */
function TicketDetail({
  entityType,
  entityId,
  token,
}: {
  entityType: string | null;
  entityId: string;
  token: string;
}) {
  const [detail, setDetail] = useState<{ text: string; meta: string[] } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (entityType === 'incidencia') {
          const i = await getIncidencia(entityId, token);
          if (alive) setDetail({
            text: i.description || '(sin descripción)',
            meta: [i.category, i.address, i.reporterName].filter(Boolean) as string[],
          });
        } else if (entityType === 'consulta') {
          const c = await getAdminConsulta(entityId, token);
          if (alive) setDetail({
            text: c.body || '(sin texto)',
            meta: [c.reporterName, c.classifiedArea?.name].filter(Boolean) as string[],
          });
        }
      } catch {
        if (alive) setDetail(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [entityType, entityId, token]);

  if (entityType !== 'incidencia' && entityType !== 'consulta') return null;
  return (
    <div className={styles.detailBox}>
      <div className={styles.detailLabel}>Detalle del ticket</div>
      {loading ? (
        <span className={styles.detailLoading}>Cargando…</span>
      ) : detail ? (
        <>
          <p className={styles.detailText}>{detail.text}</p>
          {detail.meta.length > 0 && (
            <div className={styles.detailMeta}>
              {detail.meta.map((m, i) => <span key={i}>{m}</span>)}
            </div>
          )}
        </>
      ) : (
        <span className={styles.detailLoading}>No se pudo cargar el detalle.</span>
      )}
    </div>
  );
}

/** Renderiza la respuesta del asistente como markdown (negritas, listas, links).
 *  El turno del técnico va en texto plano; solo el del asistente pasa por aquí. */
function AssistantMarkdown({ text }: { text: string }) {
  return (
    <div className={styles.md}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children }) =>
            href && /^https?:\/\//.test(href) ? (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ) : (
              <span>{children}</span>
            ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

/** Tarjeta de una acción propuesta por el agente. Reutiliza los mismos editores
 *  inline que las sugerencias de la bandeja; al aplicar corre el ejecutor del
 *  backend vía /execute (misma escritura que el botón «Hazlo»). */
function AssistantActionCardView({
  token,
  card,
  canAssign,
  busy,
  result,
  error,
  onApply,
}: {
  token: string;
  card: AssistantActionCard;
  canAssign: boolean;
  busy: boolean;
  result?: string;
  error?: string;
  onApply: (params: Record<string, unknown>) => void;
}) {
  const consultaId = card.entityId ?? '';
  return (
    <div className={styles.actionCard}>
      <div className={styles.actionCardHead}>
        <Sparkle weight="fill" size={13} color={mc.accent} />
        <span className={styles.actionCardLabel}>{card.label}</span>
      </div>
      {card.summary && <p className={styles.actionCardSummary}>{card.summary}</p>}
      {error && <div className={styles.actionCardError}>⚠ {error}</div>}

      {result ? (
        <div className={styles.result}>{result}</div>
      ) : card.actionType === 'responder_consulta' && consultaId ? (
        <ConsultaResponder
          token={token}
          consultaId={consultaId}
          busy={busy}
          onSend={(text) => onApply({ ...card.params, text })}
        />
      ) : card.actionType === 'asignar_consulta' && consultaId ? (
        canAssign ? (
          <ConsultaAssigner
            token={token}
            busy={busy}
            onAssign={(areaId, techId) =>
              onApply({ ...card.params, assigned_to: techId, ...(areaId ? { area_id: areaId } : {}) })}
          />
        ) : (
          <p className={styles.assignHint}>
            Solo un responsable con permiso para asignar puede aplicarla.
          </p>
        )
      ) : (
        <button
          type="button"
          className={`${styles.btn} ${styles.primary} ${styles.actionCardButton}`}
          disabled={busy}
          onClick={() => onApply(card.params ?? {})}
        >
          {busy ? 'Aplicando…' : 'Confirmar'}
        </button>
      )}
    </div>
  );
}

/** Tarjeta-conversación EXPANDIDA en el feed de «Conversaciones»: la
 *  conversación es un hilo de burbujas dentro de una tarjeta (no una barra
 *  lateral tipo chat). Cabecera con plegar + título/hora + borrar; cajetín de
 *  seguimiento propio abajo. */
function ThreadCard({
  token,
  thread,
  canAssign,
  apply,
  onFollowUp,
  onDelete,
  onCollapse,
}: {
  token: string;
  thread: AssistantThread;
  canAssign: boolean;
  apply: ActionApplyState;
  onFollowUp: (threadId: string, text: string) => Promise<void>;
  onDelete: (threadId: string) => void;
  onCollapse: (threadId: string) => void;
}) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setText('');
    setSending(true);
    try {
      await onFollowUp(thread.id, t);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`${styles.consultaCard} ${styles.consultaCardOpen}`}>
      <div className={styles.consultaHead}>
        <button
          type="button"
          className={styles.consultaChev}
          onClick={() => onCollapse(thread.id)}
          aria-label="Plegar conversación"
        >
          <CaretDown weight="bold" size={13} />
        </button>
        <span className={styles.consultaTitle}>{thread.title}</span>
        <span className={styles.consultaTime}>{fmtDate(thread.createdAt)}</span>
        <span className={styles.consultaHeadSpacer} />
        <button
          type="button"
          className={styles.consultaHeadClose}
          onClick={() => onDelete(thread.id)}
          aria-label="Borrar conversación"
        >
          <X weight="bold" size={14} />
        </button>
      </div>

      <div className={styles.consultaBody}>
        {thread.messages.map((m) => (
          <div
            key={m.id}
            className={`${styles.turn} ${m.role === 'user' ? styles.turnUser : styles.turnAssistant}`}
          >
            {m.role === 'assistant' && (
              <div className={styles.turnAvatar}><Sparkle weight="fill" size={14} /></div>
            )}
            <div className={styles.turnBubble}>
              {m.content &&
                (m.role === 'assistant' ? (
                  <AssistantMarkdown text={m.content} />
                ) : (
                  <p className={styles.turnText}>{m.content}</p>
                ))}
              {m.actions.map((card, idx) => (
                <AssistantActionCardView
                  key={idx}
                  token={token}
                  card={card}
                  canAssign={canAssign}
                  busy={apply.busy === `${m.id}:${idx}`}
                  result={apply.results[`${m.id}:${idx}`]}
                  error={apply.errors[`${m.id}:${idx}`]}
                  onApply={(params) => apply.onApply(m.id, idx, card.actionType, params)}
                />
              ))}
            </div>
          </div>
        ))}
        {sending && (
          <div className={`${styles.turn} ${styles.turnAssistant}`}>
            <div className={styles.turnAvatar}><Sparkle weight="fill" size={14} /></div>
            <div className={styles.turnBubble}><span className={styles.typing}>Pensando…</span></div>
          </div>
        )}
      </div>

      <div className={styles.consultaFoot}>
        <input
          className={styles.followInput}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
          }}
          placeholder="Pregunta algo más sobre esto…"
          disabled={sending}
        />
        <button
          type="button"
          className={styles.followSend}
          disabled={sending || text.trim().length === 0}
          onClick={submit}
          aria-label="Enviar"
        >
          <PaperPlaneTilt weight="fill" size={15} />
        </button>
      </div>
    </div>
  );
}

interface InboxScreenProps {
  /** Notifica al shell el nuevo nº de pendientes para el badge de la campana. */
  onUnreadChange?: (n: number) => void;
}

export function InboxScreen({ onUnreadChange }: InboxScreenProps) {
  const { state: authState } = useAuth();
  const { hasPermission } = usePermissions();
  const canAssign = hasPermission('consultas:assign');
  const token = authState.token?.accessToken ?? '';

  const [scope, setScope] = useState<AssistantScope | 'chat'>('pending');
  const [items, setItems] = useState<AssistantSuggestionApi[]>([]);
  const [counts, setCounts] = useState<{ pending: number; snoozed: number; resolved: number }>({
    pending: 0, snoozed: 0, resolved: 0,
  });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  // Briefing con criterio (LLM, Wave 2.2). null = aún no llegó → plantilla.
  const [briefing, setBriefing] = useState<{ summary: string | null } | null>(null);
  // Conversaciones: pestaña propia, feed de tarjetas-conversación (hilos 0062).
  const [convos, setConvos] = useState<AssistantThreadSummary[]>([]);            // resúmenes del feed
  const [convoFull, setConvoFull] = useState<Record<string, AssistantThread>>({}); // hilos cargados
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());        // tarjetas abiertas
  const [composerText, setComposerText] = useState('');
  const [creating, setCreating] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Al entrar en la pantalla se puede escribir sin un clic (paso 47).
  useEffect(() => { composerRef.current?.focus(); }, []);
  // El ejemplo largo, en estrecho, se corta a media palabra en una sola línea.
  const narrow = useIsNarrow();
  // Estado de aplicación de action cards (por `${messageId}:${idx}`).
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [actionResults, setActionResults] = useState<Record<string, string>>({});
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  // Modo foco «resuélvelas conmigo» (Wave 2.4): tanda de tickets de uno en
  // uno. `focusQueue` = snapshot ordenado al arrancar; null = modo apagado.
  const [focusQueue, setFocusQueue] = useState<AssistantSuggestionApi[] | null>(null);
  const [focusIndex, setFocusIndex] = useState(0);
  // Sugerencias desplegadas (plegable con chevron por item).
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());
  const toggleOpen = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const refreshCounts = useCallback(async () => {
    if (!token) return;
    try {
      const [pending, snoozed, resolved] = await Promise.all([
        listAssistantSuggestions(token, 'pending'),
        listAssistantSuggestions(token, 'snoozed'),
        listAssistantSuggestions(token, 'resolved'),
      ]);
      setCounts({ pending: pending.length, snoozed: snoozed.length, resolved: resolved.length });
      const unread = await getAssistantCount(token);
      onUnreadChange?.(unread);
    } catch {
      // Non-fatal.
    }
  }, [token, onUnreadChange]);

  const load = useCallback(async () => {
    if (!token || scope === 'chat') return; // las conversaciones se cargan aparte
    setLoading(true);
    try {
      const list = await listAssistantSuggestions(token, scope);
      setItems(list);
      // Briefing con criterio (fire-and-forget: no bloquea la lista; se refresca
      // en cada load, así refleja el estado tras asignar/responder).
      if (scope === 'pending') {
        getAssistantBriefing(token).then((b) => setBriefing({ summary: b.summary })).catch(() => {});
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, scope]);

  const refreshConvos = useCallback(async () => {
    if (!token) return;
    try {
      setConvos(await listAssistantThreads(token, 'all'));
    } catch {
      // Non-fatal: las conversaciones son secundarias frente a la bandeja.
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { refreshCounts(); }, [refreshCounts]);
  useEffect(() => { refreshConvos(); }, [refreshConvos]);

  const resolve = async (
    id: string,
    action: AssistantResolveAction,
    snoozeHours?: number,
    payload?: Record<string, unknown>,
  ) => {
    setBusyId(id);
    try {
      await resolveAssistantSuggestion(token, id, action, snoozeHours, payload);
      await load();
      await refreshCounts();
    } finally {
      setBusyId(null);
    }
  };

  // ── Modo foco «resuélvelas conmigo» (Wave 2.4) ──────────────────────
  // Arranca una tanda con las pendientes en orden de prioridad (alta→baja).
  const startFocus = () => {
    const ordered = PRIORITY_ORDER.flatMap((u) =>
      filtered.filter((s) => (s.urgency ?? 'low') === u),
    );
    if (ordered.length === 0) return;
    setFocusQueue(ordered);
    setFocusIndex(0);
  };
  const advanceFocus = () => setFocusIndex((i) => i + 1);
  const exitFocus = () => { setFocusQueue(null); setFocusIndex(0); load(); refreshCounts(); };
  // Resuelve el ticket actual y pasa al siguiente. Si el ejecutor falla
  // (p. ej. 422 sin texto), NO avanzamos: se queda para reintentar.
  const focusResolve = async (
    id: string,
    action: AssistantResolveAction,
    snoozeHours?: number,
    payload?: Record<string, unknown>,
  ) => {
    setBusyId(id);
    try {
      await resolveAssistantSuggestion(token, id, action, snoozeHours, payload);
      await refreshCounts();
      advanceFocus();
    } catch {
      // Lo dejamos en el paso actual para que el técnico corrija/reintente.
    } finally {
      setBusyId(null);
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      await scanAssistant(token);
      await load();
      await refreshCounts();
    } finally {
      setScanning(false);
    }
  };

  // Resumen de un hilo cargado, para el feed (sin volver a pedir la lista).
  const summarize = (t: AssistantThread): AssistantThreadSummary => ({
    id: t.id,
    title: t.title,
    status: t.status,
    updatedAt: t.updatedAt,
    messageCount: t.messages.length,
    lastPreview:
      [...t.messages].reverse().find((m) => m.role === 'assistant')?.content?.slice(0, 160) ?? null,
  });

  // El compositor ABRE una conversación nueva: crea el hilo, corre el agente y
  // la deja arriba del feed de «Conversaciones», expandida.
  const createThread = async () => {
    const text = composerText.trim();
    if (!text || creating) return;
    setComposerText('');
    setCreating(true);
    try {
      const t = await createAssistantThread(token, text);
      setConvoFull((prev) => ({ ...prev, [t.id]: t }));
      setExpanded((prev) => new Set(prev).add(t.id));
      setConvos((prev) => [summarize(t), ...prev]);
    } catch {
      setComposerText(text); // non-fatal: dejamos el texto para reintentar
    } finally {
      setCreating(false);
    }
  };

  const followUp = async (threadId: string, text: string) => {
    const updated = await postAssistantThreadMessage(token, threadId, text);
    setConvoFull((prev) => ({ ...prev, [threadId]: updated }));
    setConvos((prev) => prev.map((c) => (c.id === threadId ? summarize(updated) : c)));
  };

  const expandConvo = async (id: string) => {
    setExpanded((prev) => new Set(prev).add(id));
    if (!convoFull[id]) {
      try {
        const t = await getAssistantThread(token, id);
        setConvoFull((prev) => ({ ...prev, [id]: t }));
      } catch {
        setExpanded((prev) => { const n = new Set(prev); n.delete(id); return n; });
      }
    }
  };

  const collapseConvo = (id: string) =>
    setExpanded((prev) => { const n = new Set(prev); n.delete(id); return n; });

  const deleteConvo = async (id: string) => {
    try { await patchAssistantThread(token, id, 'closed'); } catch { /* non-fatal */ }
    setConvos((prev) => prev.filter((c) => c.id !== id));
    setConvoFull((prev) => { const n = { ...prev }; delete n[id]; return n; });
    collapseConvo(id);
  };

  // Al abrir «Conversaciones» por primera vez, expande la más reciente.
  const autoExpanded = useRef(false);
  useEffect(() => {
    const newest = convos[0];
    if (!autoExpanded.current && newest) {
      autoExpanded.current = true;
      expandConvo(newest.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convos]);

  // Aplica una action card tras la confirmación del técnico. Guarda el resultado
  // por `${messageId}:${idx}` y refresca la bandeja (puede auto-resolverse).
  const applyAction = async (
    messageId: string,
    idx: number,
    actionType: string,
    params: Record<string, unknown>,
  ) => {
    const key = `${messageId}:${idx}`;
    setBusyAction(key);
    setActionErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
    try {
      const { resultText } = await executeAssistantAction(token, actionType, params);
      setActionResults((prev) => ({ ...prev, [key]: resultText }));
      await load();
      await refreshCounts();
    } catch (e) {
      // Fallo (p. ej. 422 sin texto que enviar): mostramos el error PERO dejamos
      // el editor abierto para que el técnico corrija y reintente.
      const msg = e instanceof Error ? e.message : 'No se pudo aplicar la acción.';
      setActionErrors((prev) => ({ ...prev, [key]: msg }));
    } finally {
      setBusyAction(null);
    }
  };

  const applyState: ActionApplyState = {
    results: actionResults, errors: actionErrors, busy: busyAction, onApply: applyAction,
  };

  const filtered = items.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.title.toLowerCase().includes(q) || (s.context ?? '').toLowerCase().includes(q);
  });

  const convosFiltered = convos.filter((c) => {
    if (scope !== 'chat' || !search.trim()) return true;
    const q = search.toLowerCase();
    return c.title.toLowerCase().includes(q) || (c.lastPreview ?? '').toLowerCase().includes(q);
  });

  const isOpen = (s: AssistantSuggestionApi) => s.status === 'nueva' || s.status === 'mas_tarde';

  const renderCard = (s: AssistantSuggestionApi) => {
    const Icon = getModuleIcon(moduloDeOrigen(s.origin));
    const oc = getModuleColor(moduloDeOrigen(s.origin));
    const open = openIds.has(s.id);
    return (
      <div key={s.id} className={`${styles.card} ${open ? styles.cardOpen : ''}`}>
        <button
          type="button"
          className={styles.cardHead}
          onClick={() => toggleOpen(s.id)}
          aria-expanded={open}
        >
          <span className={`${styles.chev} ${open ? styles.chevOpen : ''}`}>
            <CaretDown weight="bold" size={13} />
          </span>
          <RowTile icon={Icon} accent={oc.accent} bg={oc.bg} size={34} />
          <div className={styles.cardMain}>
            <div className={styles.cardTitleRow}>
              <span className={styles.cardTitle}>{s.title}</span>
              {isOpen(s) ? (
                <Badge tone={URGENCY_TONE[s.urgency] ?? 'slate'} variant="outline">
                  {URGENCY_LABEL[s.urgency] ?? s.urgency}
                </Badge>
              ) : (
                <Badge tone={STATUS_TONE[s.status] ?? 'slate'}>
                  {STATUS_LABEL[s.status] ?? s.status}
                </Badge>
              )}
            </div>
            {!open && s.context && <p className={styles.cardContextPreview}>{s.context}</p>}
            <div className={styles.cardMeta}>
              <span>{oc.label}</span>
              <span>·</span>
              <span>{fmtDate(s.createdAt)}</span>
              {s.status === 'mas_tarde' && s.snoozeUntil && (
                <>
                  <span>·</span>
                  <span>Vuelve el {fmtDate(s.snoozeUntil)}</span>
                </>
              )}
            </div>
          </div>
        </button>

        {open && (
          <div className={styles.cardBody}>
            {s.context && <p className={styles.cardContext}>{s.context}</p>}

            {s.entityId && (
              <TicketDetail entityType={s.entityType} entityId={s.entityId} token={token} />
            )}

            {s.proposedActionLabel && isOpen(s) && (
              <div className={styles.proposal}>
                <Sparkle weight="fill" size={14} color={mc.accent} />
                <span>{s.proposedActionLabel}</span>
              </div>
            )}

            {s.resultText && <div className={styles.result}>{s.resultText}</div>}

            {isOpen(s) && s.actionType === 'responder_consulta' && s.entityId && (
              <ConsultaResponder
                token={token}
                consultaId={s.entityId}
                busy={busyId === s.id}
                onSend={(text) => resolve(s.id, 'done', undefined, { text })}
              />
            )}

            {isOpen(s) && s.actionType === 'responder_incidencia' && s.entityId && (
              <IncidenciaResponder
                token={token}
                incidenciaId={s.entityId}
                borrador={String(s.actionParams?.borrador ?? '')}
                faqPregunta={(s.actionParams?.faq_pregunta as string | undefined) ?? null}
                busy={busyId === s.id}
                onSent={() => resolve(s.id, 'done')}
              />
            )}

            {isOpen(s) && s.actionType === 'asignar_consulta' && s.entityId && canAssign && (
              <ConsultaAssigner
                token={token}
                busy={busyId === s.id}
                onAssign={(areaId, techId) =>
                  resolve(s.id, 'done', undefined, {
                    assigned_to: techId,
                    ...(areaId ? { area_id: areaId } : {}),
                  })}
              />
            )}

            {isOpen(s) && s.actionType === 'asignar_consulta' && !canAssign && (
              <p className={styles.assignHint}>
                Solo un responsable con permiso para asignar puede gestionarla. Si
                no es lo tuyo, posponla o descártala.
              </p>
            )}

            {isOpen(s) && (
              <div className={styles.actions}>
                {s.actionType !== 'responder_consulta' && s.actionType !== 'asignar_consulta' && s.actionType !== 'responder_incidencia' && (
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.primary}`}
                    disabled={busyId === s.id}
                    onClick={() => resolve(s.id, 'done')}
                  >
                    {s.canExecute
                      ? <><Sparkle weight="bold" size={15} /> Hazlo</>
                      : <><Check weight="bold" size={15} /> Hecho</>}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.btn}
                  disabled={busyId === s.id}
                  onClick={() => resolve(s.id, 'snooze', 24)}
                >
                  <Clock weight="bold" size={15} /> Más tarde
                </button>
                <button
                  type="button"
                  className={`${styles.btn} ${styles.ghost}`}
                  disabled={busyId === s.id}
                  onClick={() => resolve(s.id, 'dismiss')}
                >
                  <X weight="bold" size={15} /> Descartar
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Briefing (envoltorio conversacional, datos reales) ──────────────
  const firstName = (authState.user?.displayName ?? '').split(' ')[0] ?? '';
  const urgentN = items.filter((s) => s.urgency === 'high').length;
  const draftN = items.filter((s) => s.actionType === 'responder_consulta').length;
  const todayLabel = new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

  // ── Panel del modo foco «resuélvelas conmigo» (Wave 2.4) ────────────
  const renderFocusPanel = () => {
    if (!focusQueue) return null;
    const total = focusQueue.length;
    if (focusIndex >= total) {
      return (
        <div className={styles.focusPanel}>
          <div className={styles.focusDone}>
            <div className={styles.focusDoneIcon}><Check weight="bold" size={26} /></div>
            <h2 className={styles.focusDoneTitle}>Tanda terminada</h2>
            <p className={styles.focusDoneText}>Has repasado los {total} {total === 1 ? 'ticket' : 'tickets'} de la tanda.</p>
            <button type="button" className={`${styles.btn} ${styles.primary}`} onClick={exitFocus}>
              Volver a la bandeja
            </button>
          </div>
        </div>
      );
    }
    const s = focusQueue[focusIndex];
    if (!s) return null;
    const oc = getModuleColor(moduloDeOrigen(s.origin));
    const Icon = getModuleIcon(moduloDeOrigen(s.origin));
    const busy = busyId === s.id;
    return (
      <div className={styles.focusPanel}>
        <div className={styles.focusBar}>
          <div className={styles.focusProgress}>
            <div className={styles.focusProgressFill} style={{ width: `${(focusIndex / total) * 100}%` }} />
          </div>
          <span className={styles.focusCount}>{focusIndex + 1} de {total}</span>
          <button type="button" className={styles.focusExit} onClick={exitFocus}>Salir ✕</button>
        </div>

        <div className={styles.focusCard}>
          <div className={styles.focusHead}>
            <RowTile icon={Icon} accent={oc.accent} bg={oc.bg} size={38} />
            <div className={styles.focusHeadMain}>
              <div className={styles.focusTitleRow}>
                <span className={styles.focusTitle}>{s.title}</span>
                <Badge tone={URGENCY_TONE[s.urgency] ?? 'slate'} variant="outline">{URGENCY_LABEL[s.urgency] ?? s.urgency}</Badge>
              </div>
              <span className={styles.focusMeta}>{oc.label} · {fmtDate(s.createdAt)}</span>
            </div>
          </div>

          {s.context && <p className={styles.focusContext}>{s.context}</p>}

          {s.entityId && (
            <TicketDetail entityType={s.entityType} entityId={s.entityId} token={token} />
          )}

          {s.actionType === 'responder_consulta' && s.entityId && (
            <ConsultaResponder
              token={token}
              consultaId={s.entityId}
              busy={busy}
              onSend={(text) => focusResolve(s.id, 'done', undefined, { text })}
            />
          )}
          {s.actionType === 'responder_incidencia' && s.entityId && (
            <IncidenciaResponder
              key={s.id}
              token={token}
              incidenciaId={s.entityId}
              borrador={String(s.actionParams?.borrador ?? '')}
              faqPregunta={(s.actionParams?.faq_pregunta as string | undefined) ?? null}
              busy={busy}
              onSent={() => focusResolve(s.id, 'done')}
            />
          )}
          {s.actionType === 'asignar_consulta' && s.entityId && canAssign && (
            <ConsultaAssigner
              token={token}
              busy={busy}
              onAssign={(areaId, techId) =>
                focusResolve(s.id, 'done', undefined, { assigned_to: techId, ...(areaId ? { area_id: areaId } : {}) })}
            />
          )}
          {s.actionType === 'asignar_consulta' && !canAssign && (
            <p className={styles.assignHint}>
              Solo un responsable con permiso para asignar puede gestionarla. Sáltala o posponla.
            </p>
          )}

          <div className={styles.focusActions}>
            {s.actionType !== 'responder_consulta' && s.actionType !== 'asignar_consulta' && s.actionType !== 'responder_incidencia' && (
              <button
                type="button"
                className={`${styles.btn} ${styles.primary}`}
                disabled={busy}
                onClick={() => focusResolve(s.id, 'done')}
              >
                {s.canExecute
                  ? <><Sparkle weight="bold" size={15} /> Hazlo y siguiente</>
                  : <><Check weight="bold" size={15} /> Hecho y siguiente</>}
              </button>
            )}
            <button type="button" className={styles.btn} disabled={busy} onClick={() => focusResolve(s.id, 'snooze', 24)}>
              <Clock weight="bold" size={15} /> Más tarde
            </button>
            <button type="button" className={`${styles.btn} ${styles.ghost}`} disabled={busy} onClick={() => focusResolve(s.id, 'dismiss')}>
              <X weight="bold" size={15} /> Descartar
            </button>
            <span className={styles.focusSpacer} />
            <button type="button" className={styles.focusSkip} disabled={busy} onClick={advanceFocus}>
              Saltar →
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Paso 47: UN solo compositor, fijo encima de las pestañas, para poder
  // escribir desde que se entra sin perder el briefing. Escribir lleva a
  // «Conversaciones», que es donde aparece el hilo; la búsqueda se vacía
  // porque, si no casa con lo escrito, escondería la conversación recién abierta.
  const send = () => {
    if (!composerText.trim() || creating) return;
    setScope('chat');
    setSearch('');
    createThread();
  };

  return (
    <div
      className={styles.page}
      // El color de la IA sale de config/modules.ts, que es su único sitio.
      style={{ '--ai': mc.accent, '--ai-bg': mc.bg } as React.CSSProperties}
    >
      <Topbar
        title="Asistente IA"
        subtitle="Sugerencias proactivas para el equipo"
        moduleIcon={<Bell weight="duotone" size={22} color={mc.accent} />}
        moduleColor={mc.accent}
        moduleBg={mc.bg}
        actions={scope === 'chat'
          ? []
          : [{ label: scanning ? 'Actualizando…' : 'Actualizar sugerencias', onClick: runScan, variant: 'ghost' }]}
      />

      <div className={styles.composerBand}>
        <div className={styles.chatComposer}>
          <span className={styles.composerMark} aria-hidden="true"><Sparkle weight="fill" size={16} /></span>
          <textarea
            ref={composerRef}
            className={styles.composerField}
            value={composerText}
            onChange={(e) => setComposerText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            placeholder={narrow
              ? 'Pregunta al asistente…'
              : 'Pregunta lo que quieras al asistente… «resúmeme lo urgente», «crea un ticket para…», «asigna el KV-…»'}
            rows={1}
            disabled={creating}
          />
          <button
            type="button"
            className={styles.composerSend}
            disabled={creating || composerText.trim().length === 0}
            onClick={send}
            aria-label="Preguntar al asistente"
          >
            <PaperPlaneTilt weight="fill" size={17} />
          </button>
        </div>
      </div>

      <FilterBar
        tabs={[
          { label: 'Pendientes', value: 'pending', count: counts.pending },
          { label: 'Conversaciones', value: 'chat', count: convos.length },
          { label: 'Pospuestas', value: 'snoozed', count: counts.snoozed },
          { label: 'Resueltas', value: 'resolved', count: counts.resolved },
        ]}
        activeTab={scope}
        onTabChange={(v) => setScope(v as AssistantScope | 'chat')}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={scope === 'chat' ? 'Buscar en conversaciones…' : 'Buscar en sugerencias…'}
      />

      <div className={styles.list}>
        {loading && scope !== 'chat' && <div className={styles.empty}>Cargando…</div>}

        {/* ── Conversaciones: feed de tarjetas-conversación (separado del briefing) ── */}
        {scope === 'chat' && (
          <div className={styles.chatFeed}>
            {creating && (
              <div className={styles.consultaCard}>
                <div className={styles.consultaBody}>
                  <div className={`${styles.turn} ${styles.turnAssistant}`}>
                    <div className={styles.turnAvatar}><Sparkle weight="fill" size={14} /></div>
                    <div className={styles.turnBubble}><span className={styles.typing}>Abriendo tu consulta…</span></div>
                  </div>
                </div>
              </div>
            )}

            {!creating && convosFiltered.length === 0 && (
              <div className={styles.empty}>
                <ChatCircle weight="duotone" size={32} color={mc.accent} />
                <p>{convos.length === 0
                  ? 'Aún no has hablado con el asistente. Pregúntale algo aquí arriba.'
                  : 'Ninguna conversación coincide con la búsqueda.'}</p>
              </div>
            )}

            {convosFiltered.map((c) => {
              const full = convoFull[c.id];
              return expanded.has(c.id) && full ? (
                <ThreadCard
                  key={c.id}
                  token={token}
                  thread={full}
                  canAssign={canAssign}
                  apply={applyState}
                  onFollowUp={followUp}
                  onDelete={deleteConvo}
                  onCollapse={collapseConvo}
                />
              ) : (
                <button
                  key={c.id}
                  type="button"
                  className={styles.convoRow}
                  onClick={() => expandConvo(c.id)}
                >
                  <span className={styles.convoChev}><CaretDown weight="bold" size={13} /></span>
                  <span className={styles.convoMain}>
                    <span className={styles.convoTitle}>{c.title}</span>
                    {c.lastPreview && <span className={styles.convoPreview}>{c.lastPreview}</span>}
                  </span>
                  <span className={styles.convoMeta}>{fmtDate(c.updatedAt)}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* ── Bandeja de sugerencias (Pendientes / Pospuestas / Resueltas) ── */}
        {scope !== 'chat' && (
          scope === 'pending' && focusQueue !== null ? renderFocusPanel() : (
          <>
            {/* Cabecera-briefing: el asistente te saluda y resume (solo Pendientes) */}
            {!loading && scope === 'pending' && (
              <div className={styles.briefing}>
                <div className={styles.briefAvatar}><Sparkle weight="fill" size={19} /></div>
                <div className={styles.briefBody}>
                  <div className={styles.briefEyebrow}>Briefing · {todayLabel}</div>
                  <h2 className={styles.briefGreeting}>{greeting()}, {firstName || 'equipo'}.</h2>
                  {briefing?.summary ? (
                    // Frase con criterio del LLM (Wave 2.2). Markdown por la negrita.
                    <div className={styles.briefSummary}><AssistantMarkdown text={briefing.summary} /></div>
                  ) : (
                    <p className={styles.briefSummary}>
                      {items.length === 0 ? (
                        'Nada urgente hoy. Todo lo pendiente tiene responsable — te aviso en cuanto detecte algo.'
                      ) : (
                        <>
                          He revisado tu bandeja:{' '}
                          {urgentN > 0 && <><strong>{urgentN}</strong> {urgentN === 1 ? 'urgente' : 'urgentes'}</>}
                          {urgentN > 0 && draftN > 0 && ' y '}
                          {draftN > 0 && <><strong>{draftN}</strong> con borrador listo</>}
                          {urgentN === 0 && draftN === 0 && (
                            <><strong>{items.length}</strong> {items.length === 1 ? 'sugerencia' : 'sugerencias'}</>
                          )}
                          . {urgentN > 0 ? 'Empezaría por las urgentes.' : 'Cuando puedas, les echas un ojo.'}
                        </>
                      )}
                    </p>
                  )}
                  {filtered.length > 0 && (
                    <button type="button" className={styles.focusStart} onClick={startFocus}>
                      <Sparkle weight="bold" size={15} /> Resolver las {filtered.length} conmigo
                    </button>
                  )}
                </div>
              </div>
            )}

            {!loading && filtered.length === 0 && scope !== 'pending' && (
              <div className={styles.empty}>
                <Sparkle weight="duotone" size={32} color={mc.accent} />
                <p>No hay sugerencias en esta vista.</p>
              </div>
            )}

            {/* Pendientes: agrupadas por prioridad (Alta/Media/Baja). El resto de
                vistas (pospuestas, resueltas): lista plana. */}
            {!loading && filtered.length > 0 && scope === 'pending' &&
              PRIORITY_ORDER.map((u) => {
                const group = filtered.filter((s) => (s.urgency ?? 'low') === u);
                if (group.length === 0) return null;
                return (
                  <div key={u} className={styles.group}>
                    <div className={styles.groupHead}>
                      <span className={styles.groupDot} data-u={u} />
                      <span className={styles.groupLabel}>PRIORIDAD {URGENCY_GROUP[u]}</span>
                      <span className={styles.groupCount}>{group.length}</span>
                    </div>
                    {group.map(renderCard)}
                  </div>
                );
              })}

            {!loading && filtered.length > 0 && scope !== 'pending' && filtered.map(renderCard)}
          </>
          )
        )}
      </div>
    </div>
  );
}
